import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { useAIContext } from "@/context/AIContextProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { OpportunitiesTab, ActivityTab, SitesTab, AIInsightsTab } from "@/components/crm/CRMTabs";
import {
  Users, UsersRound, Star, Activity, TrendingDown, Loader2, Search,
  ArrowUpRight, Sparkles, ShieldAlert, RefreshCw, ExternalLink, Building2,
  AlertCircle, Briefcase, LineChart as LineChartIcon,
} from "lucide-react";
import { toast } from "sonner";
import { ProvenanceButton } from "@/components/common/ProvenanceButton";
import { DataStatusChip } from "@/components/common/DataStatusChip";
import { AIAgentPanel } from "@/components/ai/AIAgentPanel";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";


// ============================================================
// KPI card with built-in provenance overlay button (Ver1 pattern)
// ============================================================
function KpiCard({ enterpriseId, label, value, sub, icon: Icon, tone = "default", testid }) {
  const border = tone === "warn" ? "border-amber-200 bg-amber-50/40"
              : tone === "good" ? "border-emerald-200 bg-emerald-50/40"
              : tone === "danger" ? "border-rose-200 bg-rose-50/40"
              :                    "border-slate-200 bg-white";
  return (
    <div data-testid={testid} className={`rounded-lg border ${border} p-4 relative`}>
      <div className="absolute top-2 right-2">
        <ProvenanceButton enterpriseId={enterpriseId} size="xs" />
      </div>
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}


// ============================================================
// Client Health Score component (CRM-KPI-004 / 032-035)
// ============================================================
function HealthScoreCard({ score, components, enterpriseId = "CRM-KPI-004" }) {
  const tone = score >= 80 ? "text-emerald-700" : score >= 60 ? "text-amber-700" : "text-rose-700";
  const ring = score >= 80 ? "border-emerald-200 bg-emerald-50/50"
            : score >= 60 ? "border-amber-200 bg-amber-50/50"
            :                "border-rose-200 bg-rose-50/50";
  return (
    <div data-testid="crm-health-card" className={`rounded-lg border p-4 ${ring} relative`}>
      <div className="absolute top-2 right-2">
        <ProvenanceButton enterpriseId={enterpriseId} size="xs" />
      </div>
      <div className="flex items-center gap-2 text-slate-500">
        <Sparkles className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-medium">Client Health Score</span>
        <DataStatusChip status="ai_generated" size="xs" className="ml-auto mr-6" />
      </div>
      <div className={`text-4xl font-display font-semibold mt-2 ${tone}`}>{score}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">AI composite · 0-100</div>
      <div className="mt-4 space-y-2">
        {[
          { key: "revenue_trend",     label: "Revenue trend",      eid: "CRM-KPI-032" },
          { key: "payment_behaviour", label: "Payment behaviour",  eid: "CRM-KPI-033" },
          { key: "activity",          label: "Activity",           eid: "CRM-KPI-034" },
          { key: "issue",             label: "Issue score",        eid: "CRM-KPI-035" },
        ].map(({ key, label, eid }) => {
          const v = components?.[key] ?? 0;
          return (
            <div key={key} className="flex items-center gap-2 text-xs">
              <span className="w-32 text-slate-600 truncate flex items-center gap-1">
                {label}
                <ProvenanceButton enterpriseId={eid} size="xs" />
              </span>
              <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className={`h-full ${v >= 80 ? "bg-emerald-500" : v >= 60 ? "bg-amber-500" : "bg-rose-500"}`}
                     style={{ width: `${v}%` }} />
              </div>
              <span className="w-8 text-right text-slate-700 font-medium">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ============================================================
// Client detail slide-over
// ============================================================
function ClientDetailSheet({ clientId, open, onClose, onMetaChanged }) {
  const { role } = usePermissions();
  const canEditMeta = ["executive", "admin", "finance_admin", "ops_mgmt", "bu_manager"].includes((role || "").toLowerCase());
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(false);

  const toggleKeyClient = async (checked) => {
    if (!detail) return;
    setSavingKey(true);
    try {
      await api.post(`/crm/clients/${detail.id}/meta`, { is_key_client: checked });
      setDetail((d) => ({ ...d, is_key_client: checked }));
      onMetaChanged?.(detail.id, { is_key_client: checked });
      toast.success(checked ? `${detail.name} flagged as Key Client` : `${detail.name} removed from Key Clients`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update key-client flag");
    } finally { setSavingKey(false); }
  };

  useEffect(() => {
    if (!clientId || !open) { setDetail(null); return; }
    setLoading(true);
    api.get(`/crm/clients/${clientId}`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load client"))
      .finally(() => setLoading(false));
  }, [clientId, open]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Client detail</span>
            {detail?.is_key_client && <Badge className="bg-amber-500 hover:bg-amber-600 text-white text-[10px]">Key client</Badge>}
            {detail?.declining_flag && <Badge className="bg-rose-500 hover:bg-rose-600 text-white text-[10px]">Declining activity</Badge>}
          </div>
          <SheetTitle className="font-display text-lg">
            {loading ? "Loading…" : detail?.name}
          </SheetTitle>
          {detail && canEditMeta && (
            <label className="flex items-center gap-2 mt-2 cursor-pointer select-none w-fit"
                   data-testid="crm-key-client-toggle-label">
              <Switch checked={!!detail.is_key_client} onCheckedChange={toggleKeyClient}
                      disabled={savingKey} data-testid="crm-key-client-toggle" />
              <span className="text-xs text-slate-600 flex items-center gap-1">
                <Star className={`w-3.5 h-3.5 ${detail.is_key_client ? "text-amber-500 fill-amber-500" : "text-slate-400"}`} />
                Key client
              </span>
            </label>
          )}
          <SheetDescription className="sr-only">
            Detailed CRM view for the selected client — revenue trend, jobs, quotes and health score
          </SheetDescription>
          <div className="mt-3">
            {detail && (
              <AIAgentPanel
                agentId="crm"
                agentName="CRM Agent"
                context={{ client_id: detail.id }}
                defaultQuestion={`Give me a 30-second briefing on ${detail.name}. Highlight anything unusual, cross-sell opportunities, and immediate actions.`}
                buttonLabel={`Analyse ${detail.name}`}
                buttonVariant="filled"
                buttonSize="sm"
                testid="crm-detail-ai-btn"
              />
            )}
          </div>
          {detail?.aroflo_external_id && (
            <span className="text-xs text-slate-500 font-mono">AroFlo ID · {detail.aroflo_external_id}</span>
          )}
        </SheetHeader>

        <div className="px-6 py-5 space-y-5">
          {loading && (<div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading client…</div>)}
          {detail && !loading && (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <KpiCard enterpriseId="CRM-KPI-013" label="Revenue 12M" value={fmtCurrency(detail.revenue_12m)} icon={LineChartIcon} testid="crm-detail-revenue-12m" />
                <KpiCard enterpriseId="CRM-KPI-023" label="Open jobs" value={fmtNumber(detail.jobs?.open ?? 0)} icon={Briefcase} testid="crm-detail-open-jobs" />
                <KpiCard enterpriseId="CRM-KPI-020" label="Last job" value={(detail.jobs?.last_job_at || "").slice(0, 10) || "—"} icon={Activity} testid="crm-detail-last-job" />
                <KpiCard enterpriseId="CRM-KPI-026" label="Open quotes" value={fmtCurrency(detail.quotes?.open_value ?? 0)} sub={`${detail.quotes?.open_count ?? 0} quotes`} icon={ArrowUpRight} testid="crm-detail-open-quotes" />
                <KpiCard enterpriseId="CRM-KPI-027" label="Conversion %" value={`${detail.quotes?.conversion_pct ?? 0}%`} icon={TrendingDown} testid="crm-detail-conversion" />
                <KpiCard enterpriseId="CRM-KPI-025" label="Reactive %" value={`${detail.jobs?.reactive_pct ?? 0}%`} sub="vs PPM · last 90 days" icon={AlertCircle} testid="crm-detail-reactive" />
              </div>

              {/* Health */}
              <HealthScoreCard score={detail.health?.overall ?? 0} components={detail.health?.components || {}} />

              {/* 24m revenue chart (CRM-KPI-014) */}
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <LineChartIcon className="w-3.5 h-3.5" />
                  <span className="text-[10px] uppercase tracking-wider">24-month revenue trend</span>
                  <ProvenanceButton enterpriseId="CRM-KPI-014" size="xs" className="ml-auto" />
                </div>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={detail.revenue_series_24m}>
                      <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} tickMargin={4} interval={2} />
                      <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={45} />
                      <Tooltip formatter={(v) => fmtCurrency(v)} />
                      <Line type="monotone" dataKey="revenue" stroke="#003a5c" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Recent jobs (CRM-KPI-024) */}
              <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                  <Briefcase className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Recent jobs</span>
                  <ProvenanceButton enterpriseId="CRM-KPI-024" size="xs" className="ml-auto" />
                </div>
                {(detail.recent_jobs || []).length === 0 ? (
                  <div className="p-4 text-sm text-slate-500">No recent jobs on file for this client.</div>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                    {(detail.recent_jobs || []).slice(0, 12).map((j) => (
                      <div key={j.id} className="px-4 py-2 text-xs flex items-center gap-3 hover:bg-slate-50/60">
                        <span className="font-mono text-[10px] text-slate-500 w-20">{String(j.job_number || "").slice(0, 12)}</span>
                        <span className="flex-1 truncate text-slate-800">{typeof j.title === "string" && j.title ? j.title : "—"}</span>
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{typeof j.status === "string" ? j.status : "unknown"}</Badge>
                        <span className="text-[10px] text-slate-500 w-24 text-right">{(j.created_at || "").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}


// ============================================================
// TAB: Coming soon (per Ver1)
// ============================================================
function ComingSoonTab({ label, ids = [] }) {
  return (
    <div className="p-6 rounded-lg border border-dashed border-slate-300 bg-slate-50/40 text-sm text-slate-600 space-y-3">
      <div className="flex items-center gap-2 text-slate-700 font-medium">
        <Sparkles className="w-4 h-4 text-amber-500" />
        {label} · scoped in Enterprise Data Dictionary Ver1
      </div>
      <p className="text-xs text-slate-500 max-w-xl">
        This tab will populate once the underlying AI agent and source integrations are wired. The KPIs below
        already live in your ingested dictionary — click the ⓘ icon to inspect the full spec.
      </p>
      <div className="flex flex-wrap gap-2 pt-1">
        {ids.map((id) => (
          <div key={id} className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-1">
            {id}
            <ProvenanceButton enterpriseId={id} size="xs" />
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================
// MAIN CRM PAGE
// ============================================================
export default function CRMPage() {
  const { can, loaded } = usePermissions();
  const { setContext } = useAIContext();
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [keyClients, setKeyClients] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [keyOnly, setKeyOnly] = useState(false);
  const [tab, setTab] = useState("key");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const PAGE_SIZE = 500;

  const fetchClients = async (pageArg, qArg, keyArg, append) => {
    const params = new URLSearchParams({ page: String(pageArg), page_size: String(PAGE_SIZE) });
    if (qArg && qArg.trim()) params.set("q", qArg.trim());
    if (keyArg) params.set("key_only", "true");
    const r = await api.get(`/crm/clients?${params.toString()}`, { timeout: 90000 });
    setServerTotal(r.data.total ?? 0);
    setClients((prev) => (append ? [...prev, ...(r.data.items || [])] : r.data.items || []));
  };

  const fetchKeyClients = async () => {
    // Key clients are fetched server-side — they may live on any page of the
    // 14k+ client list, so filtering the loaded page misses them.
    const r = await api.get("/crm/clients?page=1&page_size=1000&key_only=true", { timeout: 90000 });
    setKeyClients(r.data.items || []);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s] = await Promise.all([
        api.get("/crm/summary", { timeout: 90000 }),
        fetchClients(1, q, keyOnly, false),
        fetchKeyClients(),
      ]);
      setPage(1);
      setSummary(s.data);
      setContext({ module: "crm", summary: `CRM · ${s.data.total_clients} clients · ${s.data.key_clients} key` });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load CRM");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (loaded && can("crm", "view")) load(); /* eslint-disable-next-line */ }, [loaded, keyOnly]);

  // Server-side search — debounced so 20k+ client bases are searched in Mongo,
  // not just within the loaded page.
  useEffect(() => {
    if (!loaded || !can("crm", "view")) return;
    const t = setTimeout(() => {
      setPage(1);
      fetchClients(1, q, keyOnly, false).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      await fetchClients(next, q, keyOnly, true);
      setPage(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load more clients");
    } finally { setLoadingMore(false); }
  };

  const filteredKey = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return keyClients;
    return keyClients.filter((c) => (c.name || "").toLowerCase().includes(s));
  }, [keyClients, q]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter((c) => (c.name || "").toLowerCase().includes(s));
  }, [clients, q]);

  const dormant = useMemo(() => clients.filter((c) => c.declining_flag), [clients]);
  const critical = useMemo(() => clients.filter((c) => c.health_score < 60), [clients]);

  if (!loaded) return <AppLayout title="CRM · Clients"><div className="h-64 rounded-lg bg-white border border-slate-200 shimmer" /></AppLayout>;
  if (!can("crm", "view")) {
    return (
      <AppLayout title="CRM · Clients">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">You don't have access to CRM.</h2>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="CRM · Clients"
      subtitle="Ver1 dashboard — 41 KPIs · Client Health · Cross-sell insights"
      actions={
        <div className="flex items-center gap-2">
          <AIAgentPanel
            agentId="crm"
            agentName="CRM Agent"
            context={{}}
            buttonLabel="Ask CRM Agent"
            buttonVariant="filled"
            buttonSize="sm"
            testid="crm-ai-agent-btn"
          />
          <NavLink to="/crm/master">
            <Button size="sm" variant="outline" data-testid="crm-nav-master">
              <Building2 className="w-3.5 h-3.5 mr-1.5" /> Master data
            </Button>
          </NavLink>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="crm-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      }
    >
      {/* KPI strip — 4 headline metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
        <KpiCard enterpriseId="CRM-KPI-005" testid="crm-kpi-total-clients"
          label="Total clients" value={fmtNumber(summary?.total_clients ?? 0)}
          sub={`${summary?.active_clients ?? 0} active (180d)`} icon={Users} />
        <KpiCard enterpriseId="CRM-KPI-001" testid="crm-kpi-key-clients"
          label="Key clients" value={fmtNumber(summary?.key_clients ?? 0)}
          sub="Manager-flagged" icon={Star} tone="good" />
        <KpiCard enterpriseId="CRM-KPI-036" testid="crm-kpi-dormant"
          label="Dormant / declining" value={fmtNumber(dormant.length)}
          sub="No jobs 90 days" icon={TrendingDown} tone={dormant.length > 0 ? "warn" : "default"} />
        <KpiCard enterpriseId="CRM-KPI-004" testid="crm-kpi-at-risk"
          label="Clients at risk" value={fmtNumber(critical.length)}
          sub="Health < 60" icon={AlertCircle} tone={critical.length > 0 ? "danger" : "default"} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap gap-1 justify-start">
          <TabsTrigger data-testid="crm-tab-key" value="key" className="text-xs data-[state=active]:bg-white">Key Clients</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-all" value="all" className="text-xs data-[state=active]:bg-white">All Clients</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-health" value="health" className="text-xs data-[state=active]:bg-white">Client Health</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-opportunities" value="opportunities" className="text-xs data-[state=active]:bg-white">Opportunities</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-activity" value="activity" className="text-xs data-[state=active]:bg-white">Client Activity</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-contacts" value="contacts" className="text-xs data-[state=active]:bg-white">Contacts</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-sites" value="sites" className="text-xs data-[state=active]:bg-white">Sites</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-issues" value="issues" className="text-xs data-[state=active]:bg-white">Issues / Actions</TabsTrigger>
          <TabsTrigger data-testid="crm-tab-ai" value="ai" className="text-xs data-[state=active]:bg-white">AI Insights</TabsTrigger>
        </TabsList>

        {/* ---------- Key Clients ---------- */}
        <TabsContent value="key" className="mt-4">
          <ClientTable
            data={filteredKey}
            q={q} setQ={setQ}
            keyOnly={keyOnly} setKeyOnly={setKeyOnly}
            onOpen={(c) => setSelectedId(c.id)}
            serverTotal={serverTotal} loadedCount={clients.length}
            onLoadMore={loadMore} loadingMore={loadingMore}
            emptyState="No clients are flagged as Key Clients yet. Open a client and toggle 'Key client' in the detail view."
          />
        </TabsContent>

        {/* ---------- All Clients ---------- */}
        <TabsContent value="all" className="mt-4">
          <ClientTable
            data={filtered}
            q={q} setQ={setQ}
            keyOnly={keyOnly} setKeyOnly={setKeyOnly}
            onOpen={(c) => setSelectedId(c.id)}
            serverTotal={serverTotal} loadedCount={clients.length}
            onLoadMore={loadMore} loadingMore={loadingMore}
            emptyState="No clients yet. Data flows in from the Aroflo / MYOB syncs — see Master data."
          />
        </TabsContent>

        {/* ---------- Client Health ---------- */}
        <TabsContent value="health" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...clients].sort((a, b) => a.health_score - b.health_score).slice(0, 12).map((c) => (
              <div key={c.id} data-testid={`crm-health-${c.id}`}
                   className="bg-white border border-slate-200 rounded-lg p-3 hover:border-slate-300 cursor-pointer transition-colors"
                   onClick={() => setSelectedId(c.id)}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900 truncate">{c.name}</div>
                  <span className={`text-xl font-display font-semibold ${
                    c.health_score >= 80 ? "text-emerald-700"
                    : c.health_score >= 60 ? "text-amber-700"
                    : "text-rose-700"
                  }`}>{c.health_score}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{fmtCurrency(c.revenue_12m)} · {c.open_jobs} open jobs</div>
                <div className="mt-3 h-1 rounded bg-slate-200 overflow-hidden">
                  <div className={`h-full ${c.health_score >= 80 ? "bg-emerald-500" : c.health_score >= 60 ? "bg-amber-500" : "bg-rose-500"}`}
                       style={{ width: `${c.health_score}%` }} />
                </div>
                {c.declining_flag && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">
                    <TrendingDown className="w-3 h-3" /> Declining activity
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ---------- Ver1 stub tabs ---------- */}
        <TabsContent value="opportunities" className="mt-4"><OpportunitiesTab /></TabsContent>
        <TabsContent value="activity"      className="mt-4"><ActivityTab /></TabsContent>
        <TabsContent value="contacts"      className="mt-4"><ComingSoonTab label="Contacts"      ids={["CRM-KPI-016", "CRM-KPI-017"]} /></TabsContent>
        <TabsContent value="sites"         className="mt-4"><SitesTab /></TabsContent>
        <TabsContent value="issues"        className="mt-4"><ComingSoonTab label="Issues / Actions" ids={["CRM-KPI-037", "CRM-KPI-038", "CRM-KPI-039"]} /></TabsContent>
        <TabsContent value="ai"            className="mt-4"><AIInsightsTab /></TabsContent>
      </Tabs>

      <ClientDetailSheet
        clientId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onMetaChanged={(id, patch) => {
          setClients((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
          if ("is_key_client" in patch) {
            if (patch.is_key_client) {
              const src = clients.find((c) => c.id === id);
              setKeyClients((ks) => (ks.some((k) => k.id === id) ? ks
                : [...ks, { ...(src || { id }), ...patch }].sort((a, b) => (a.name || "").localeCompare(b.name || ""))));
            } else {
              setKeyClients((ks) => ks.filter((k) => k.id !== id));
            }
          }
        }}
      />
    </AppLayout>
  );
}


// ============================================================
// Reusable Client table (Key + All tabs)
// ============================================================
function ClientTable({ data, q, setQ, keyOnly, setKeyOnly, onOpen, emptyState,
                        serverTotal = 0, loadedCount = 0, onLoadMore, loadingMore = false }) {
  const { sorted, sort, setSort } = useSortable(data);
  const [visible, setVisible] = useState(300);
  useEffect(() => { setVisible(300); }, [q, keyOnly, data.length]);
  const windowed = sorted.slice(0, visible);
  const moreOnServer = loadedCount < serverTotal;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-[280px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search clients…" className="pl-9 h-9 bg-white" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" data-testid="crm-key-only" checked={keyOnly}
                 onChange={(e) => setKeyOnly(e.target.checked)} />
          Key clients only
        </label>
        <div className="ml-auto text-xs text-slate-500">
          {data.length.toLocaleString()} shown{serverTotal > loadedCount ? ` · ${serverTotal.toLocaleString()} total` : ""}
        </div>
      </div>

      {data.length === 0 ? (
        <div className="p-6 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
          {emptyState}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <SortableTh label="Client" k="name" sort={sort} setSort={setSort} className="text-left font-medium px-3 py-2" />
                  <SortableTh label="Health" k="health_score" sort={sort} setSort={setSort} className="text-left font-medium px-3 py-2" />
                  <SortableTh label="Revenue 12M" k="revenue_12m" sort={sort} setSort={setSort} className="text-right font-medium px-3 py-2" />
                  <SortableTh label="Open jobs" k="open_jobs" sort={sort} setSort={setSort} className="text-right font-medium px-3 py-2" />
                  <SortableTh label="Reactive %" k="reactive_pct" sort={sort} setSort={setSort} className="text-right font-medium px-3 py-2" />
                  <SortableTh label="Open quotes" k="open_quote_value" sort={sort} setSort={setSort} className="text-right font-medium px-3 py-2" />
                  <SortableTh label="Status" k="declining_flag" sort={sort} setSort={setSort} className="text-left font-medium px-3 py-2" />
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {windowed.map((c) => (
                  <tr key={c.id}
                      data-testid={`crm-client-row-${c.id}`}
                      onClick={() => onOpen(c)}
                      className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {c.is_key_client && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                        <span className="font-medium text-slate-900">{c.name}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono">{c.aroflo_external_id || "—"}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1 rounded bg-slate-200 overflow-hidden">
                          <div className={`h-full ${c.health_score >= 80 ? "bg-emerald-500" : c.health_score >= 60 ? "bg-amber-500" : "bg-rose-500"}`}
                               style={{ width: `${c.health_score}%` }} />
                        </div>
                        <span className={`text-xs font-medium ${c.health_score >= 80 ? "text-emerald-700" : c.health_score >= 60 ? "text-amber-700" : "text-rose-700"}`}>
                          {c.health_score}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(c.revenue_12m)}</td>
                    <td className="px-3 py-2 text-right">{c.open_jobs}</td>
                    <td className="px-3 py-2 text-right">{c.reactive_pct}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(c.open_quote_value)}</td>
                    <td className="px-3 py-2">
                      {c.declining_flag ? (
                        <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">Declining</Badge>
                      ) : c.is_key_client ? (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px]">Key</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px]">Active</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right"><ExternalLink className="w-3.5 h-3.5 text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(sorted.length > visible || moreOnServer) && (
            <div className="px-3 py-2.5 border-t border-slate-100 flex items-center justify-between gap-3 bg-slate-50/60">
              <span className="text-xs text-slate-500">
                Showing {Math.min(visible, sorted.length).toLocaleString()} of {sorted.length.toLocaleString()} loaded
                {moreOnServer ? ` · ${serverTotal.toLocaleString()} total — use search to find any client` : ""}
              </span>
              <div className="flex items-center gap-2">
                {sorted.length > visible && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="crm-show-more-btn"
                          onClick={() => setVisible((v) => v + 500)}>
                    Show 500 more
                  </Button>
                )}
                {sorted.length <= visible && moreOnServer && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="crm-load-more-btn"
                          onClick={onLoadMore} disabled={loadingMore}>
                    {loadingMore ? "Loading…" : "Load 500 more"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
